/**
 * Vendor service for fetching vendor and truck information
 */

import { createApiClient } from '../utils/apiClient';
import { envLog } from '../config/environment';

export interface TruckLocation {
  objectId: string;
  name: string;
  defaultLocation: {
    formatted_address: string;
  };
  currentStatus: 'OPEN' | 'OPEN-OFFLINE' | 'CLOSED';
  isCorporate?: boolean;
}

export interface VendorInfo {
  truckList: TruckLocation[];
  vendorName?: string;
  vendorId: string;
}

export interface VendorGetInfoRequest {
  vendorId: string;
  clientType: 'WEBAPP';
}

export interface VendorGetInfoResponse {
  result?: {
    truckList: TruckLocation[];
    vendorName?: string;
    [key: string]: any;
  };
  truckList?: TruckLocation[];
  vendorName?: string;
  [key: string]: any; // Allow for additional fields in the response
}

/**
 * Fetch vendor information including truck list
 * @param vendorId - The vendor identifier
 * @param environment - The environment to use for API calls
 * @returns Promise resolving to vendor information with truck list
 */
export const fetchVendorInfo = async (
  vendorId: string,
  environment: string = 'app'
): Promise<VendorInfo> => {
  console.log('🚚 fetchVendorInfo called with:', { vendorId, environment });
  const apiClient = createApiClient(environment);
  
  try {
    envLog(environment, 'Fetching vendor info', { vendorId });
    
    const requestBody: VendorGetInfoRequest = {
      vendorId,
      clientType: 'WEBAPP'
    };
    
    // Make API call to get vendor information
    const response = await apiClient.post<VendorGetInfoResponse>(
      '/parse/functions/vendorGetInfo',
      requestBody
    );
    
    envLog(environment, 'Vendor info fetched successfully', response.data);
    
    // Log the structure of the response for debugging
    envLog(environment, 'Response structure analysis', {
      hasResult: !!response.data.result,
      hasTruckList: !!response.data.truckList,
      resultType: typeof response.data.result,
      truckListType: typeof response.data.truckList,
      resultKeys: response.data.result ? Object.keys(response.data.result) : 'N/A',
      directTruckList: response.data.truckList,
      resultTruckList: response.data.result?.truckList,
    });
    
    // Parse Parse Server response format - the actual data might be in response.data.result
    const actualData = response.data.result || response.data;
    let truckList = actualData.truckList || [];
    
    envLog(environment, 'Initial truck list', { truckList, count: truckList.length });
    
    // Filter out corporate trucks if there are multiple trucks
    if (truckList.length > 1) {
      const filteredTruckList = truckList.filter(truck => !truck.isCorporate);
      envLog(environment, 'Filtering corporate trucks', { 
        originalCount: truckList.length,
        filteredCount: filteredTruckList.length,
        corporateTrucksRemoved: truckList.length - filteredTruckList.length
      });
      truckList = filteredTruckList;
    }
    
    envLog(environment, 'Final truck list', { truckList, count: truckList.length });
    
    return {
      vendorId,
      truckList: truckList,
      vendorName: actualData.vendorName,
    };
    
  } catch (error) {
    envLog(environment, 'Failed to fetch vendor info, falling back to mock data', error);
    console.error('API Error - falling back to mock data:', error);
    
    // If API call fails, fall back to mock data for development/testing
    return generateMockVendorInfo(vendorId, environment);
  }
};

/**
 * Generate mock vendor data for development and testing
 * This will be used as fallback when the real API is not available
 */
const generateMockVendorInfo = (
  vendorId: string,
  environment: string
): VendorInfo => {
  envLog(environment, 'Generating mock vendor info', { vendorId });
  
  const mockTruckList: TruckLocation[] = [
    {
      objectId: 'truck_001',
      name: 'Downtown Food Truck',
      defaultLocation: {
        formatted_address: '123 Main Street, Downtown, CA 90210'
      },
      currentStatus: 'OPEN'
    },
    {
      objectId: 'truck_002', 
      name: 'Campus Corner Cafe',
      defaultLocation: {
        formatted_address: '456 University Ave, College Town, CA 90211'
      },
      currentStatus: 'CLOSED'
    },
    {
      objectId: 'truck_003',
      name: 'Beachside Bites',
      defaultLocation: {
        formatted_address: '789 Ocean Blvd, Beach City, CA 90212'
      },
      currentStatus: 'OPEN-OFFLINE'
    },
    {
      objectId: 'truck_004',
      name: 'Corporate Catering',
      defaultLocation: {
        formatted_address: '999 Corporate Plaza, Business District, CA 90213'
      },
      currentStatus: 'OPEN',
      isCorporate: true
    }
  ];
  
  return {
    vendorId,
    vendorName: `Mock Vendor ${vendorId}`,
    truckList: mockTruckList,
  };
};
